import styled from '@emotion/styled';
import { useEffect, useRef, useState } from 'react';

import { RouteManager } from '../../RouteManager';
import { IconButton } from '../buttons/IconButton';
import { ListBox } from '../list-box/ListBox';
import { Dialog } from '../overlay/Dialog';

import { useI18n } from '../../hooks/useI18nFormatters';

import { cssClass } from '../../theme/cssClass';
import { glyph } from '../../theme/glyph';
import { theme } from '../../theme/theme';

import { PremiumRequiredDialogItem } from './PremiumRequiredDialogItem';

export function PremiumRequiredDialog() {
  const videoRef = useRef(null);
  const { i18n } = useI18n();
  const [progress, setProgress] = useState(0);
  const [iteration, setIteration] = useState(1);

  const items = [
    {
      key: 'unlimited-devices',
      title: 'premium.premiumRequiredDialog.unlimitedDevicesTitle',
      subtitle: 'premium.premiumRequiredDialog.unlimitedDevicesSubtitle',
      url: glyph.system.premium,
      video: 'https://etc.athom.com/homey-premium/unlimited-devices-480x480.mp4',
      context: {},
    },
    {
      key: 'advanced-flow',
      title: 'premium.premiumRequiredDialog.advancedFlowTitle',
      subtitle: 'premium.premiumRequiredDialog.advancedFlowSubtitle',
      url: glyph.system.advancedFlow,
      video: 'https://etc.athom.com/homey-premium/advanced-flow-480x480.mp4',
      context: {},
    },
    {
      key: 'homey-insights',
      title: 'premium.premiumRequiredDialog.homeyInsightsTitle',
      subtitle: 'premium.premiumRequiredDialog.homeyInsightsSubtitle',
      url: glyph.system.insights,
      video: 'https://etc.athom.com/homey-premium/insights-480x480.mp4',
      context: {},
    },
    {
      key: 'logic-and-variables',
      title: 'premium.premiumRequiredDialog.logicVariablesTitle',
      subtitle: 'premium.premiumRequiredDialog.logicVariablesSubtitle',
      url: glyph.system.logic,
      video: 'https://etc.athom.com/homey-premium/logic-variables-480x480.mp4',
      context: {},
    },
  ];

  const defaultItem = items[0];

  useEffect(() => {
    const videoElement = videoRef.current;

    function handleTimeUpdate() {
      setProgress(videoElement.currentTime / videoElement.duration);
    }

    function handleEnded() {
      setIteration((iteration) => iteration + 1);
      setProgress(0);
      videoRef.current.play();
    }

    videoElement.addEventListener('timeupdate', handleTimeUpdate);
    videoElement.addEventListener('ended', handleEnded);

    return () => {
      videoElement.removeEventListener('timeupdate', handleTimeUpdate);
      videoElement.removeEventListener('ended', handleEnded);
    };
  }, []);

  function handleClose() {
    RouteManager.pushState({});
  }

  function handleSelectionChange(keys, listState) {
    const [key] = keys;
    const item = listState.collection.getItem(key);

    if (item != null) {
      setProgress(0);
      setIteration(1);
      videoRef.current.src = item.value.video;
      videoRef.current.load();
      videoRef.current.play();
    }
  }

  return (
    <Dialog onClose={handleClose} isOpen>
      <S.Root className={cssClass.lightTheme}>
        <S.Close
          url={glyph.core.cross}
          color={theme.color.icon_light}
          size={theme.icon.size_default}
          onPress={() => {
            handleClose();
          }}
        />
        <S.Header>
          <S.Title>{i18n.messageFormatter('premium.premiumRequiredDialog.title')}</S.Title>
          <ListBox
            aria-label="Premium Feature Select"
            selectionMode="single"
            items={items}
            renderItem={() => {
              return <PremiumRequiredDialogItem progress={progress} iteration={iteration} />;
            }}
            disallowEmptySelection={true}
            defaultSelectedKeys={[defaultItem.key]}
            onSelectionChange={handleSelectionChange}
          />
        </S.Header>
        <S.Content>
          <S.Video ref={videoRef} autoPlay muted>
            <source src={defaultItem.video} type="video/mp4" />
          </S.Video>
        </S.Content>
      </S.Root>
    </Dialog>
  );
}

function S() {}
PremiumRequiredDialog.S = S;

S.Root = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  box-shadow: ${theme.boxShadow.default};
  border-radius: ${theme.borderRadius.default};
  height: auto;
  width: 100%;
  max-width: 896px;
  text-align: start;
  line-height: 1.5;
`;

S.Close = styled(IconButton)`
  position: absolute;
  top: 1rem;
  right: 1rem;
`;

S.Header = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  padding: 3rem;
  border-radius: ${theme.borderRadius.default} 0 0 ${theme.borderRadius.default};
  background: ${theme.color.body};
  color: ${theme.color.text};
`;

S.Title = styled.h1`
  margin-bottom: 1.5rem;
  font-size: ${theme.fontSize.heading1};
  background: linear-gradient(
    30deg,
    ${theme.color.gradient_pink_start},
    ${theme.color.gradient_pink_end}
  );
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;

S.Content = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 0;
  background: ${theme.color.white};
  border-radius: 0 ${theme.borderRadius.default} ${theme.borderRadius.default} 0;
`;

S.Video = styled.video`
  width: 480px;
  height: 480px;
`;
